import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ResultsFilters from './features/results/components/ResultsFilters';
import useCompetitionDetails from './features/results/hooks/useCompetitionDetails';
import useMobileCheck from './useMobileCheck';
import {
  buildGroupOptions,
  deriveSelectionState,
  matchGroupValue,
} from './features/results/utils/selection';
import { serializeGroupValue } from './features/results/utils/group';

const CompetitionInfoCard = ({ EventID, onNavigateToResult }) => {
  const isMobile = useMobileCheck();
  const { data: competitions } = useCompetitionDetails(EventID);
  const [selection, setSelection] = useState({
    eventId: EventID,
    competitionId: null,
    competitionName: '',
    classificationId: null,
    stageId: null,
    stageStatus: null,
    stageStatusDesc: '',
    stageIsTested: 0,
    group: [],
  });
  const [filterIndexes, setFilterIndexes] = useState({
    competition: 0,
    classification: 0,
    stage: 0,
  });
  const [groupOptions, setGroupOptions] = useState([]);

  const resolvedSelection = useMemo(
    () => deriveSelectionState(competitions, selection),
    [competitions, selection]
  );

  useEffect(() => {
    if (!resolvedSelection) {
      setGroupOptions([]);
      return;
    }

    setFilterIndexes({
      competition: resolvedSelection.competitionIndex,
      classification: resolvedSelection.classificationIndex,
      stage: resolvedSelection.stageIndex,
    });
    setGroupOptions(resolvedSelection.groupOptions);
    setSelection((prev) => {
      const next = { ...prev, ...resolvedSelection.selectionPayload };
      const sameGroup =
        serializeGroupValue(prev.group) === serializeGroupValue(next.group);
      if (
        prev.competitionId === next.competitionId &&
        prev.classificationId === next.classificationId &&
        prev.stageId === next.stageId &&
        prev.stageStatus === next.stageStatus &&
        prev.stageStatusDesc === next.stageStatusDesc &&
        prev.stageIsTested === next.stageIsTested &&
        sameGroup
      ) {
        return prev;
      }
      return next;
    });
  }, [resolvedSelection]);

  useEffect(() => {
    if (!resolvedSelection || typeof onNavigateToResult !== 'function') {
      return;
    }

    const payload = resolvedSelection.selectionPayload;
    onNavigateToResult(
      payload.competitionName,
      EventID,
      payload.competitionId,
      payload.group,
      payload.stageId,
      payload.stageStatus,
      payload.stageStatusDesc,
      payload.classificationId,
      payload.stageIsTested,
      resolvedSelection.groupOptions
    );
  }, [EventID, onNavigateToResult, resolvedSelection]);

  const handleCompetitionChange = useCallback(
    (competitionIndex) => {
      const competition = competitions[competitionIndex];
      if (!competition) {
        return;
      }

      const classificationIndex = 0;
      const stageIndex = 0;
      const classification = competition.Classifications?.[classificationIndex];
      const stage = competition.Stages?.[stageIndex];
      const options = buildGroupOptions(classification);
      const groupValue = matchGroupValue(options, selection.group);

      setFilterIndexes({
        competition: competitionIndex,
        classification: classificationIndex,
        stage: stageIndex,
      });
      setGroupOptions(options);
      setSelection((prev) => ({
        ...prev,
        competitionId: competition.IDCompetition,
        competitionName: competition.NameCompetition,
        classificationId: classification?.ClassificationID || null,
        stageId: stage?.StageID || null,
        stageStatus: stage?.StageStatus || null,
        stageStatusDesc: stage?.StageStatusDesc || '',
        stageIsTested: stage?.IsTested || 0,
        group: groupValue,
      }));
    },
    [competitions, selection.group]
  );

  const handleClassificationChange = useCallback(
    (classificationIndex) => {
      const competition = competitions[filterIndexes.competition];
      const classification = competition?.Classifications?.[classificationIndex];

      if (!classification) {
        return;
      }

      const options = buildGroupOptions(classification);
      const groupValue = matchGroupValue(options, selection.group);

      setFilterIndexes((prev) => ({ ...prev, classification: classificationIndex }));
      setGroupOptions(options);
      setSelection((prev) => ({
        ...prev,
        classificationId: classification.ClassificationID,
        group: groupValue,
      }));
    },
    [competitions, filterIndexes.competition, selection.group]
  );

  const handleGroupChange = useCallback((groupValue) => {
    setSelection((prev) => ({ ...prev, group: groupValue }));
  }, []);

  const handleStageChange = useCallback(
    (stageIndex) => {
      const competition = competitions[filterIndexes.competition];
      const stage = competition?.Stages?.[stageIndex];

      if (!stage) {
        return;
      }

      setFilterIndexes((prev) => ({ ...prev, stage: stageIndex }));
      setSelection((prev) => ({
        ...prev,
        stageId: stage.StageID,
        stageStatus: stage.StageStatus,
        stageStatusDesc: stage.StageStatusDesc,
        stageIsTested: stage.IsTested,
      }));
    },
    [competitions, filterIndexes.competition]
  );

  if (!competitions.length) {
    return <div className="notification has-text-centered">Brak danych</div>;
  }

  return (
    <ResultsFilters
      competitions={competitions}
      competitionIndex={filterIndexes.competition}
      classificationIndex={filterIndexes.classification}
      stageIndex={filterIndexes.stage}
      groupOptions={groupOptions}
      selectedGroupValue={selection.group}
      onCompetitionChange={handleCompetitionChange}
      onClassificationChange={handleClassificationChange}
      onGroupChange={handleGroupChange}
      onStageChange={handleStageChange}
      isMobile={isMobile}
    />
  );
};

export default CompetitionInfoCard;
